import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Alert, Theme } from '@arc-ui/components';
import classNames from 'classnames';

import { ModalTypes } from 'features/order-tracker/shared-components/Modal/constants';
import { CloseIcon } from 'features/order-tracker/shared-components/svg';
import {
  closeAmendAppointmentModal,
  openModal,
  setNotificationType,
  updateAppointmentById,
} from 'features/order-tracker/store/actions';
import { useTrackModalLoadAnalytics } from 'features/order-tracker/store/hooks';
import { MS_IN_DAY } from 'features/order-tracker/store/sagas/services/helpers/constants';
import {
  getAmendAppointmentInfo,
  getCurrentAppointmentInfoToAmend,
  getOrderSummary,
} from 'features/order-tracker/store/selectors';
import { formatDate, getMsBeforeMidday, toggleMainWindowScroll } from 'features/order-tracker/utils';

import { AmendAppointment } from './AmendAppointment';
import { AmendsConfirmation } from './AmendsConfirmation';
import { SectionHeader } from './components';
import { WARNING_PONR, WARNING_PONR_ALT } from './components/Notification/constants';
import {
  AMENDS_CONFIRMATION_SUBTITLE,
  AMENDS_CONFIRMATION_TITLE,
  AMEND_APPOINTMENT_TITLE,
  AMEND_LIVE_CHAT,
  AMEND_LIVE_CHAT_DESCRIPTION,
  AMEND_LIVE_CHAT_HEADING,
  ANALYTICS_APPT_CLOSE,
  ANALYTICS_APPT_MODAL,
  ANALYTICS_APPT_MODAL_CLOSE,
} from './constants';

import styles from './styles.module.scss';

export const AmendAppointmentPage: React.FC = () => {
  const dispatch = useDispatch();
  const { isReadyToConfirm, selectedSlot, addingNewContact, selectedContact, initialContactKey, selectedDate } =
    useSelector(getAmendAppointmentInfo);
  const { appointmentsInfo } = useSelector(getOrderSummary);
  const { originDate, itemId } = useSelector(getCurrentAppointmentInfoToAmend);
  const [ponrNotificationWasShown, setPonrNotificationWasShown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLDivElement>(null);
  const newContactSelected = initialContactKey !== selectedContact;
  const hasSaveAmendsButton = !addingNewContact && (!!selectedSlot || newContactSelected);
  const pageTitle = isReadyToConfirm ? AMENDS_CONFIRMATION_TITLE : AMEND_APPOINTMENT_TITLE;
  const pageSubtitle = isReadyToConfirm ? AMENDS_CONFIRMATION_SUBTITLE : undefined;

  const openCancellationAmendingModal = (): void => {
    dispatch(openModal({ modalType: ModalTypes.AmendAppointment }));
  };

  const checkDates = () => {
    // if there is 2 days diffrence between  expertsetup & engineer Appointment
    const EngineerAppointments = appointmentsInfo.engineerAppointments;
    const GetExpertSetupAppointment = EngineerAppointments.find(
      (appointment) => appointment.displayTitle === 'Expert Setup',
    );
    const GetExpertSetupAppointmentDate = GetExpertSetupAppointment?.engineeringAppointmentDate ?? '';

    const date1: any = new Date(GetExpertSetupAppointmentDate);
    const date2: any = new Date(selectedDate);
    const diffTime = Math.abs(date2 - date1);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const finishAmendAppointmentFlow = useCallback((event: Event): void => {
    event.stopPropagation();
    dispatch(closeAmendAppointmentModal());
    dispatch(updateAppointmentById({ itemId, updatedApptData: { isDateLessThan24Hours: true } }));
    dispatch(openModal({ modalType: ModalTypes.PONRModal }));
  }, []);

  const handleOnClick = () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.sprChat('enable');
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.sprChat('open');
  };

  useEffect(() => {
    toggleMainWindowScroll();
    return () => toggleMainWindowScroll();
  }, []);

  useEffect(() => {
    let timerId;
    const isApptTomorrow = formatDate('DD/MM/YYYY', originDate) === formatDate('DD/MM/YYYY', new Date(Date.now() + MS_IN_DAY));
    const timeBeforeMidday = getMsBeforeMidday();

    if (isApptTomorrow && timeBeforeMidday) {
      !ponrNotificationWasShown && dispatch(setNotificationType(isReadyToConfirm ? WARNING_PONR : WARNING_PONR_ALT));
      timerId = setTimeout(
        () => document.addEventListener('click', finishAmendAppointmentFlow, true),
        timeBeforeMidday,
      );
      setPonrNotificationWasShown(true);
    }

    return () => {
      document.removeEventListener('click', finishAmendAppointmentFlow, true);
      clearTimeout(timerId);
    };
  }, [isReadyToConfirm]);

  useTrackModalLoadAnalytics(containerRef, ANALYTICS_APPT_MODAL);

  useEffect(() => {
    closeButtonRef?.current?.setAttribute(
      'data-analytics-link',
      isReadyToConfirm ? ANALYTICS_APPT_MODAL_CLOSE : ANALYTICS_APPT_CLOSE,
    );
  }, [isReadyToConfirm]);

  return (
    <div ref={containerRef} className={styles.modalPageWindow}>
      <div
        className={classNames(styles.modalPageContainer, {
          [styles.hasSaveAmendsButton]: hasSaveAmendsButton,
          [styles.confirmationPage]: isReadyToConfirm,
        })}
      >
        <SectionHeader title={pageTitle} subtitle={pageSubtitle} className={styles.amendAppointmentTitle} hasBigTitle />
        <div
          ref={closeButtonRef}
          className={styles.closeButton}
          onClick={openCancellationAmendingModal}
          onKeyDown={openCancellationAmendingModal}
          role="button"
          tabIndex={0}
        >
          <CloseIcon height={32} width={32} />
        </div>
        {isReadyToConfirm && checkDates() < 2 && (
          <Theme >
            <div className={styles.banner}>
              <Alert
                description={AMEND_LIVE_CHAT_DESCRIPTION}
                heading={AMEND_LIVE_CHAT_HEADING}
                headingLevel="4"
                isDismissable
                links={[{ href: "#", children: AMEND_LIVE_CHAT, screenReaderText: "Some more description about the link", onClick: () => handleOnClick() }]}
                status="warning"
              />
            </div>
          </Theme >
        )}
        {isReadyToConfirm ? <AmendsConfirmation /> : <AmendAppointment />}
      </div>
    </div>
  );
};
